import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDarkMode } from "../../../context/DarkModeProvider";
import "../../../assets/css/colors.css";
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useLanguage } from "context/LanguageProvider";
import { index } from "d3";
import { Alert } from "reactstrap";
import { setGameDone } from "apiHandles/user_progress";

const SearchGame = () => {
    const { darkMode } = useDarkMode();
    const [currentStage, setCurrentStage] = useState(3);
    const [score, setScore] = useState(0);
    const [lives, setLives] = useState(3);
    const [gameState, setGameState] = useState("playing");
    const [message, setMessage] = useState("");
    const [showMessage, setShowMessage] = useState(false);
    const [messageType, setMessageType] = useState("info");

    // Linear Search State
    const [linearArray] = useState([15, 3, 8, 12, 7, 20, 5]);
    const [linearTarget] = useState(12);
    const [visitedNodes, setVisitedNodes] = useState([]);
    const [draggedItems, setDraggedItems] = useState([]);
    const [showQuestions, setShowQuestions] = useState(false);
    const [questionAnswers, setQuestionAnswers] = useState({});
    const [showQuestionResults, setShowQuestionResults] = useState(false);
    const [questionResults, setQuestionResults] = useState({});
    const [specialWinCandidate, setSpecialWinCandidate] = useState(false);
    const [binaryArray] = useState([2, 5, 8, 12, 16, 23, 28, 35, 42, 51, 67]);
    const [binaryTarget] = useState(16); // Αλλαγή από 23 σε 16
    const [binaryNotFoundTarget] = useState(25);
    const [currentLeft, setCurrentLeft] = useState(0);
    const [currentRight, setCurrentRight] = useState(10); // Διόρθωση: 10 αντί για 13
    const [currentMid, setCurrentMid] = useState(null);
    const [binaryVisited, setBinaryVisited] = useState([]);
    const [waitingForMidChoice, setWaitingForMidChoice] = useState(false);
    const [waitingForDirectionChoice, setWaitingForDirectionChoice] = useState(false);
    const [timeLeft, setTimeLeft] = useState(15);
    const [binaryComplete, setBinaryComplete] = useState(false);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);

    const { selLanguage, toggleLanguage, isLoading } = useLanguage();

    const linearQuestions = [
        {
            id: 1,
            question: selLanguage
                ? "Ποια είναι η χρονική πολυπλοκότητα της γραμμικής αναζήτησης στη χειρότερη περίπτωση;"
                : "What is the time complexity of linear search in the worst case?",
            options: selLanguage
                ? ["O(1)", "O(log n)", "O(n)", "O(n²)"]
                : ["O(1)", "O(log n)", "O(n)", "O(n²)"],
            correct: 2
        },
        {
            id: 2,
            question: selLanguage
                ? "Πότε χρησιμοποιούμε τη γραμμική αναζήτηση;"
                : "When do we use linear search?",
            options: selLanguage
                ? [
                    "Μόνο σε ταξινομημένους πίνακες",
                    "Μόνο σε αταξινόμητους πίνακες",
                    "Σε οποιονδήποτε πίνακα",
                    "Μόνο σε μικρούς πίνακες"
                ]
                : [
                    "Only on sorted arrays",
                    "Only on unsorted arrays",
                    "On any array",
                    "Only on small arrays"
                ],
            correct: 2
        },
        {
            id: 3,
            question: selLanguage
                ? "Στη βέλτιστη περίπτωση, η γραμμική αναζήτηση έχει πολυπλοκότητα:"
                : "In the best case, linear search has complexity:",
            options: selLanguage
                ? ["O(n)", "O(log n)", "O(1)", "O(n log n)"]
                : ["O(n)", "O(log n)", "O(1)", "O(n log n)"],
            correct: 2
        }
    ];

    useEffect(() => {
        if (currentStage === 2 || currentStage === 3) {
            resetBinarySearch();
            setTimeout(() => startBinaryStep(), 500);
        }
    }, [currentStage]);


    const showTemporaryMessage = (msg, type = "info", duration = 3000) => {
        setMessage(msg);
        setMessageType(type); // Προσθέτουμε αυτό
        setShowMessage(true);
        setTimeout(() => {
            setShowMessage(false);
        }, duration);
    };

    useEffect(() => {
        let timer;
        if ((waitingForMidChoice || waitingForDirectionChoice) && timeLeft > 0) {
            timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
        } else if ((waitingForMidChoice || waitingForDirectionChoice) && timeLeft === 0) {
            setLives(prev => prev - 1);
            showTemporaryMessage(selLanguage ? "⏰ Ο χρόνος τελείωσε! Χάσατε μία ζωή. Προσπαθήστε ξανά." : "⏰ Time is up! You lost a life. Try again."
                , "error");
            if (lives - 1 === 0) {
                setShowGameOver(true);
            } else {
                setTimeLeft(15);
                setWaitingForMidChoice(false);
                setWaitingForDirectionChoice(false);
                setTimeout(() => startBinaryStep(), 1500);
            }
        }
        return () => clearTimeout(timer);
    }, [timeLeft, waitingForMidChoice, waitingForDirectionChoice, lives]);

    const resetBinarySearch = () => {
        setCurrentLeft(0);
        setCurrentRight(binaryArray.length - 1);
        setCurrentMid(null);
        setBinaryVisited([]);
        setWaitingForMidChoice(false);
        setWaitingForDirectionChoice(false);
        setTimeLeft(15);
        setBinaryComplete(false);
        setSpecialWinCandidate(false); // 👈 πρόσθεσε αυτό
    };


    const handleDragStart = (e, nodeValue, index) => {
        e.dataTransfer.setData("text/plain", JSON.stringify({ value: nodeValue, originalIndex: index }));
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const data = JSON.parse(e.dataTransfer.getData("text/plain"));

        const expectedIndex = draggedItems.length;
        const correctSequence = [0, 1, 2, 3];

        if (expectedIndex < correctSequence.length && data.originalIndex === correctSequence[expectedIndex]) {
            setDraggedItems(prev => [...prev, data]);
            setVisitedNodes(prev => [...prev, data.originalIndex]);

            if (data.value === linearTarget) {
                setScore(prev => prev + 20);
                showTemporaryMessage(selLanguage ? "Βρήκατε το στοιχείο! Απαντήστε στις ερωτήσεις:" : " You found the element! Answer the questions:"
                    , "success");
                setShowQuestions(true);
            }
        } else {
            setLives(prev => prev - 1);
            showTemporaryMessage(selLanguage ? "Λάθος σειρά! Προσπαθήστε ξανά." : " Wrong order! Try again."
                , "error");
            if (lives - 1 === 0) {
                setShowGameOver(true);
            }
            setDraggedItems([]);
            setVisitedNodes([]);
        }
    };

    const handleQuestionAnswer = (questionId, answerIndex) => {
        setQuestionAnswers(prev => ({ ...prev, [questionId]: answerIndex }));
    };

    const submitQuestions = () => {
        let correctCount = 0;
        const results = {};

        linearQuestions.forEach(q => {
            const isCorrect = questionAnswers[q.id] === q.correct;
            results[q.id] = isCorrect;
            if (isCorrect) {
                correctCount++;
            }
        });

        setQuestionResults(results);
        const successRate = correctCount / linearQuestions.length;
        setScore(prev => prev + correctCount * 10);

        setShowQuestionResults(true);

        setTimeout(() => {
            setShowQuestionResults(false);

            if (successRate >= 0.5) {
                if (correctCount === linearQuestions.length) {
                    showTemporaryMessage(selLanguage ? "Τέλειο! Προχωράτε στο επόμενο στάδιο!" : " Perfect! Proceeding to the next stage!"
                        , "success");
                } else {
                    showTemporaryMessage(selLanguage ? ` ${correctCount}/${linearQuestions.length} σωστές απαντήσεις. Προχωράτε στο επόμενο στάδιο!` : ` ${correctCount}/${linearQuestions.length} correct answers. Proceeding to the next stage!`
                        , "success");
                }
                setTimeout(() => {
                    setCurrentStage(2);
                    // Ξεκινάμε το binary search
                    setTimeout(() => startBinaryStep(), 500);
                }, 2000);
                setShowQuestions(false);
                setQuestionAnswers({});
            } else {
                showTemporaryMessage(selLanguage ? `Χρειάζεστε τουλάχιστον 50% επιτυχία! Έχετε ${correctCount}/${linearQuestions.length} σωστές απαντήσεις. Δοκιμάστε ξανά!` : `You need at least 50% success! You have ${correctCount}/${linearQuestions.length} correct answers. Try again!`
                    , "error");
                setLives(prev => Math.max(0, prev - 1));
                if (lives - 1 === 0) {
                    setShowGameOver(true);
                }
                setQuestionAnswers({});
                setDraggedItems([]);
                setVisitedNodes([]);
            }
        }, 3000);
    };

    const startBinaryStep = () => {
        if (currentLeft > currentRight) {
            // Το εύρος αναζήτησης έχει εξαντληθεί
            const target = currentStage === 2 ? binaryTarget : binaryNotFoundTarget;
            const found = binaryVisited.some(idx => binaryArray[idx] === target);

            if (currentStage === 2) {
                // Stage 2: Ψάχνουμε το 16 (υπάρχει)
                if (found) {
                    setBinaryComplete(true);
                    setScore(prev => prev + 30);
                    showTemporaryMessage(
                        selLanguage ? "✅ Σωστά! Βρήκατε το στοιχείο 16!" : "✅ Correct! You found element 16!",
                        "success"
                    );
                    setTimeout(() => {
                        setCurrentStage(3);
                        resetBinarySearch();
                        setTimeout(() => startBinaryStep(), 500);
                    }, 2000);
                } else {
                    setLives(prev => prev - 1);
                    showTemporaryMessage(
                        selLanguage ? "❌ Δεν εξερευνήσατε σωστά! Το στοιχείο υπάρχει!" : "❌ You didn't search correctly! The element exists!",
                        "error"
                    );
                    if (lives - 1 === 0) {
                        setShowGameOver(true);
                    } else {
                        resetBinarySearch();
                        setTimeout(() => startBinaryStep(), 1500);
                    }
                }
            } else if (currentStage === 3) {
                // Stage 3: Ψάχνουμε το 25 (ΔΕΝ υπάρχει)
                if (!found) {
                    setBinaryComplete(true);
                    setScore(prev => prev + 30);
                    showTemporaryMessage(
                        selLanguage ? "✅ Σωστά! Το στοιχείο 25 δεν υπάρχει στον πίνακα!" : "✅ Correct! Element 25 does NOT exist in the array!",
                        "success"
                    );
                    setTimeout(() => {
                        setGameState("won");
                        setShowGameWon(true);
                        showTemporaryMessage(
                            selLanguage ? "🎉 Συγχαρητήρια! Ολοκληρώσατε όλα τα στάδια!" : "🎉 Congratulations! You completed all stages!",
                            "success"
                        );
                    }, 2000);
                } else {
                    setLives(prev => prev - 1);
                    showTemporaryMessage(
                        selLanguage ? "❌ Λάθος! Το στοιχείο δεν υπάρχει αλλά εσείς το βρήκατε!" : "❌ Wrong! The element doesn't exist but you found it!",
                        "error"
                    );
                    if (lives - 1 === 0) {
                        setShowGameOver(true);
                    } else {
                        resetBinarySearch();
                        setTimeout(() => startBinaryStep(), 1500);
                    }
                }
            }
            return;
        }

        setWaitingForMidChoice(true);
        setTimeLeft(15);
    };

    const handleBinaryNodeClick = (index) => {

        if (!waitingForMidChoice || binaryComplete || index < currentLeft || index > currentRight) return;

        const correctMid = Math.floor((currentLeft + currentRight) / 2);
        const target = currentStage === 2 ? binaryTarget : binaryNotFoundTarget;

        setCurrentMid(index);
        setBinaryVisited(prev => [...prev, index]);
        setWaitingForMidChoice(false);
        setTimeLeft(15);

        if (currentStage === 3 && binaryArray[index] === 28) {
            setGameState("won");
        }

        if (index !== correctMid) {
            setLives(prev => prev - 1);
            showTemporaryMessage(
                selLanguage
                    ? ` Λάθος επιλογή! Ο σωστός μεσαίος κόμβος είναι ο ${correctMid}. Χάσατε μία ζωή.`
                    : ` Wrong choice! The correct middle node is ${correctMid}. You lost a life.`,
                "error"
            );
            if (lives - 1 === 0) {
                setShowGameOver(true);
                return;
            }
            setCurrentMid(correctMid);
            setBinaryVisited(prev => [...prev, correctMid]);

            setTimeout(() => {
                if (binaryArray[correctMid] === target) {
                    setBinaryComplete(true);
                    setScore(prev => prev + 25);
                    showTemporaryMessage(
                        selLanguage ? " Βρήκατε το στοιχείο! Προχωράτε στο επόμενο στάδιο!" : " You found the element! Proceeding to the next stage!",
                        "success"
                    );
                    setTimeout(() => {
                        setCurrentStage(3);
                        resetBinarySearch();
                        setTimeout(() => startBinaryStep(), 500);
                    }, 2000);
                } else {
                    setWaitingForDirectionChoice(true);
                    setTimeLeft(8);
                    setMessage(
                        selLanguage
                            ? `Ελέγξατε το στοιχείο ${binaryArray[correctMid]}. Που πρέπει να αναζητήσετε;`
                            : `You checked the element ${binaryArray[correctMid]}. Where should you search?`
                    );
                    setShowMessage(true);
                }
            }, 2000);
            return;
        }

        // Σωστή επιλογή μεσαίου
        if (binaryArray[index] === target) {
            setBinaryComplete(true);
            setScore(prev => prev + 35);
            showTemporaryMessage(
                selLanguage
                    ? " Τέλεια! Επιλέξατε τον σωστό μεσαίο κόμβο και βρήκατε το στοιχείο! Προχωράτε στο επόμενο στάδιο!"
                    : " Perfect! You selected the correct middle node and found the element! Proceeding to the next stage!",
                "success"
            );
            setTimeout(() => {
                setCurrentStage(3);
                resetBinarySearch();
                setTimeout(() => startBinaryStep(), 500);
            }, 2000);
        } else {
            setScore(prev => prev + 10);

            // 👇 Αν είμαστε στο Level 3 και ο κόμβος είναι 28, ενεργοποιούμε "υποψήφιο" special win
            if (currentStage === 3 && binaryArray[index] === 28) {
                setSpecialWinCandidate(true);
            } else {
                setSpecialWinCandidate(false);
            }

            setWaitingForDirectionChoice(true);
            setTimeLeft(8);
            setMessage(
                selLanguage
                    ? ` Σωστή επιλογή μεσαίου! Ελέγξατε το στοιχείο ${binaryArray[index]}. Που πρέπει να αναζητήσετε;`
                    : ` Correct mid choice! You checked the element ${binaryArray[index]}. Where should you search?`
            );
            setShowMessage(true);
        }
    };

    const handleBinaryChoice = (choice) => {

        if (!waitingForDirectionChoice || currentMid === null) return;

        setWaitingForDirectionChoice(false);
        setTimeLeft(15); // Reset timer
        const target = currentStage === 2 ? binaryTarget : binaryNotFoundTarget;

        const correctChoice = binaryArray[currentMid] < target ? "right" : "left";

        // Υπολογίζουμε το νέο εύρος τοπικά (θα το εφαρμόσουμε μετά)
        let newLeft = currentLeft;
        let newRight = currentRight;

        if (choice === correctChoice) {
            // Ο χρήστης επέλεξε σωστά -> εφαρμόζουμε την επιλεγμένη πλευρά
            setScore(prev => prev + 10);
            if (choice === "left") {
                newRight = currentMid - 1;
            } else {
                newLeft = currentMid + 1;
            }
            showTemporaryMessage(
                selLanguage ? " Σωστή επιλογή!" : " Correct choice!",
                "success"
            );
        } else {
            setSpecialWinCandidate(false);

            setLives(prev => prev - 1);
            showTemporaryMessage(
                selLanguage
                    ? ` Λάθος επιλογή! Η σωστή πλευρά είναι: ${correctChoice.toUpperCase()}. Αφαιρείται μία ζωή.`
                    : ` Wrong choice! The correct side is: ${correctChoice.toUpperCase()}. You lost a life.`,
                "error"
            );
            if (lives - 1 === 0) {
                setShowGameOver(true);
                return;
            }

            if (correctChoice === "left") {
                newRight = currentMid - 1;
            } else {
                newLeft = currentMid + 1;
            }
        }

        if (binaryArray[index] === 28) {
            setGameState("won");

        }
        setCurrentLeft(newLeft);
        setCurrentRight(newRight);
        if (currentStage === 3 && specialWinCandidate && newLeft > newRight) {
            setBinaryComplete(true);
            setScore(prev => prev + 30);
            setGameState("won");
            setShowGameWon(true);
            showTemporaryMessage(
                selLanguage ? "🎉 Συγχαρητήρια! Ολοκληρώσατε όλα τα στάδια!" : "🎉 Congratulations! You completed all stages!",
                "success"
            );
            return;
        }

        // Συνέχεια κανονικής ροής
        setTimeout(startBinaryStep, 1000);
    };

    const resetGame = () => {
        setCurrentStage(1);
        setScore(0);
        setLives(3);
        setGameState("playing");
        setVisitedNodes([]);
        setDraggedItems([]);
        setShowQuestions(false);
        setQuestionAnswers({});
        setShowQuestionResults(false);
        setQuestionResults({});
        setTimeLeft(15);
        setWaitingForMidChoice(false);
        setWaitingForDirectionChoice(false);
        resetBinarySearch();
        showTemporaryMessage(selLanguage ? " Στάδιο 1: Γραμμική Αναζήτηση - Σύρετε τους κόμβους με τη σωστή σειρά επίσκεψης!" : " Stage 1: Linear Search - Drag the nodes in the correct visitation order!");
        setShowGameOver(false);
        setSpecialWinCandidate(false);
        setShowGameWon(false);
    };

    const getMessageStyle = (type) => {
        const baseStyle = {
            padding: "15px 25px",
            borderRadius: "12px",
            fontWeight: "600",
            fontSize: "16px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
            border: "2px solid",
            width: "70%",
            textAlign: "center",
            alignContent: "center",
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
        };

        switch (type) {
            case "success":
                return {
                    ...baseStyle,
                    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    color: "white",
                    borderColor: "#059669"
                };
            case "error":
                return {
                    ...baseStyle,
                    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    color: "white",
                    borderColor: "#dc2626"
                };
            default:
                return {
                    ...baseStyle,
                    background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                    color: "white",
                    borderColor: "#1d4ed8"
                };
        }
    };

    const renderLinearSearchStage = () => (
        <div style={{ padding: "20px" }}>
            <div style={{
                display: "flex",
                justifyContent: "center",
                gap: "10px",
                marginBottom: "30px",
                flexWrap: "wrap"
            }}>
                {linearArray.map((value, index) => (
                    <motion.div
                        key={index}
                        draggable={!showQuestions && gameState === "playing"}
                        onDragStart={(e) => handleDragStart(e, value, index)}
                        style={{
                            width: "60px",
                            height: "60px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "12px",
                            fontWeight: "bold",
                            fontSize: "18px",
                            border: "2px solid",
                            background: visitedNodes.includes(index)
                                ? value === linearTarget
                                    ? "linear-gradient(45deg, #10b981, #059669)"
                                    : "linear-gradient(45deg, #0219b3ff, #010e7cff)"
                                : "linear-gradient(45deg, #e2e8f0, #cbd5e1)",
                            color: visitedNodes.includes(index) ? "white" : "#334155",
                            cursor: !showQuestions && gameState === "playing" ? "grab" : "default",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            borderColor: visitedNodes.includes(index)
                                ? value === linearTarget ? "#059669" : "#c9c2c2ff"
                                : "#bfc0c1ff"
                        }}
                        whileHover={!showQuestions && gameState === "playing" ? { scale: 1.05 } : {}}
                        whileDrag={{ scale: 1.1, rotate: 5 }}
                    >
                        {value}
                    </motion.div>
                ))}
            </div>

            <div
                style={{
                    minHeight: "80px",
                    border: "3px dashed #3b82f6",
                    borderRadius: "12px",
                    background: "#f0f9ff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    padding: "20px",
                    marginBottom: "20px"
                }}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
            >
                {draggedItems.length === 0 ? (
                    <p style={{ color: "#3b82f6", fontWeight: "600" }}>
                        {selLanguage ? " Σύρετε εδώ τους κόμβους με τη σειρά επίσκεψης"
                            : " Drag the nodes here in the order of visitation"}
                    </p>
                ) : (
                    draggedItems.map((item, index) => (
                        <div
                            key={index}
                            style={{
                                width: "50px",
                                height: "50px",
                                background: "linear-gradient(45deg, #3b82f6, #2563eb)",
                                color: "white",
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: "bold"
                            }}
                        >
                            {item.value}
                        </div>
                    ))
                )}
            </div>

            {showQuestions && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{
                        background: "#efedebff",
                        padding: "20px",
                        borderRadius: "12px",
                        border: "2px solid #3b82f6"
                    }}
                >
                    <h3 style={{ color: "#180901ff", marginBottom: "20px", textAlign: "center" }}>
                        {selLanguage ? "Ερωτήσεις Γραμμικής Αναζήτησης" : "Linear Search Questions"}
                    </h3>
                    {linearQuestions.map((q) => (
                        <div key={q.id} style={{ marginBottom: "20px" }}>
                            <p style={{ fontWeight: "600", marginBottom: "10px", color: "#080210ff" }}>
                                {q.id}. {q.question}
                            </p>
                            <div style={{ display: "grid", gap: "8px" }}>
                                {q.options.map((option, index) => {
                                    let backgroundColor = "#f8fafc";
                                    let borderColor = "#e2e8f0";

                                    if (questionAnswers[q.id] === index) {
                                        backgroundColor = "#dbeafe";
                                        borderColor = "#3b82f6";
                                    }

                                    return (
                                        <label
                                            key={index}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                padding: "8px",
                                                background: backgroundColor,
                                                borderRadius: "8px",
                                                cursor: showQuestionResults ? "default" : "pointer",
                                                border: "2px solid",
                                                borderColor: borderColor
                                            }}
                                        >
                                            <input
                                                type="radio"
                                                name={`question-${q.id}`}
                                                checked={questionAnswers[q.id] === index}
                                                onChange={() => handleQuestionAnswer(q.id, index)}
                                                disabled={showQuestionResults}
                                            />
                                            {option}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                    <div style={{ textAlign: "center" }}>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={submitQuestions}
                            disabled={Object.keys(questionAnswers).length < linearQuestions.length || showQuestionResults}
                            style={{
                                background: Object.keys(questionAnswers).length < linearQuestions.length || showQuestionResults
                                    ? "#94a3b8" : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                color: "white",
                                padding: "12px 24px",
                                borderRadius: "8px",
                                border: "none",
                                fontWeight: "600",
                                cursor: Object.keys(questionAnswers).length < linearQuestions.length || showQuestionResults
                                    ? "not-allowed" : "pointer"
                            }}
                        >
                            {showQuestionResults ? "Επεξεργασία..." : " Υποβολή Απαντήσεων"}
                        </motion.button>
                    </div>
                </motion.div>
            )}
        </div>
    );
    const renderBinarySearchStage = () => {
        const target = currentStage === 2 ? binaryTarget : binaryNotFoundTarget;

        return (
            <div
                style={{
                    padding: "20px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "100%"
                }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "5px",
                        flexWrap: "wrap",
                        marginBottom: "30px"
                    }}
                >
                    {binaryArray.map((value, index) => {
                        let backgroundColor, borderColor, textColor, isClickable;

                        if (index === currentMid && !binaryComplete) {
                            backgroundColor = "linear-gradient(45deg, #f59e0b, #d97706)";
                            borderColor = "#d97706";
                            textColor = "white";
                            isClickable = false;
                        } else if (binaryVisited.includes(index)) {
                            if (value === target && binaryComplete) {
                                backgroundColor = "linear-gradient(45deg, #10b981, #059669)";
                                borderColor = "#059669";
                                textColor = "white";
                            } else {
                                backgroundColor = "linear-gradient(45deg, #e5e7eb, #d1d5db)";
                                borderColor = "#9ca3af";
                                textColor = "#374151";
                            }
                            isClickable = false;
                        } else if (index < currentLeft || index > currentRight) {
                            backgroundColor = "linear-gradient(45deg, #fca5a5, #f87171)";
                            borderColor = "#f87171";
                            textColor = "white";
                            isClickable = false;
                        } else {
                            backgroundColor = "linear-gradient(45deg, #dbeafe, #93c5fd)";
                            borderColor = "#60a5fa";
                            textColor = "#1e40af";
                            isClickable = waitingForMidChoice && !binaryComplete;
                        }

                        return (
                            <div
                                key={index}
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: "5px"
                                }}
                            >
                                <motion.div
                                    onClick={() => handleBinaryNodeClick(index)}
                                    style={{
                                        width: "50px",
                                        height: "50px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: isClickable ? "pointer" : "default",
                                        background: backgroundColor,
                                        color: textColor,
                                        borderRadius: "8px",
                                        fontWeight: "bold",
                                        fontSize: "14px",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                        border: "2px solid",
                                        borderColor: borderColor
                                    }}
                                    whileHover={isClickable ? { scale: 1.1 } : {}}
                                    animate={
                                        index === currentMid && !binaryComplete
                                            ? { scale: [1, 1.1, 1], transition: { duration: 0.5 } }
                                            : {}
                                    }
                                >
                                    {value}
                                </motion.div>
                                <div
                                    style={{
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        color: darkMode ? "#9ca3af" : "#6b7280"
                                    }}
                                >
                                    {index}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {(waitingForMidChoice || waitingForDirectionChoice) && (
                    <div
                        style={{
                            textAlign: "center",
                            marginBottom: "20px"
                        }}
                    >
                        <div
                            style={{
                                background: darkMode ? "#1f2937" : "#e2e8f0",
                                color: darkMode ? "white" : "#1e293b",
                                padding: "15px 25px",
                                borderRadius: "12px",
                                fontWeight: "bold",
                                fontSize: "1.2em",
                                display: "inline-block"
                            }}
                        >
                            {timeLeft} {selLanguage ? "δευτερόλεπτα" : "seconds"}
                        </div>
                    </div>
                )}

                {waitingForDirectionChoice && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: "20px",
                            marginBottom: "20px",
                            flexWrap: "wrap"
                        }}
                    >
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleBinaryChoice("left")}
                            style={{
                                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                                color: "white",
                                padding: "15px 25px",
                                borderRadius: "12px",
                                border: "none",
                                fontWeight: "600",
                                cursor: "pointer"
                            }}
                        >
                            ⬅ {selLanguage ? "Αριστερό Τμήμα" : "Left Half"}
                        </motion.button>

                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleBinaryChoice("right")}
                            style={{
                                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                color: "white",
                                padding: "15px 25px",
                                borderRadius: "12px",
                                border: "none",
                                fontWeight: "600",
                                cursor: "pointer"
                            }}
                        >
                            {selLanguage ? "Δεξί Τμήμα" : "Right Half"}
                        </motion.button>
                    </motion.div>
                )}
            </div>
        );
    };




    return (
        <div style={{
            minHeight: "100vh",
            background: darkMode
                ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)"
                : "var(--lightModeGameBackground)",
            padding: "30px 10px",
            borderRadius: "10px",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            overflowX: "hidden"
        }}>
            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={resetGame}
            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={resetGame}
                score={score}
            />

            <div style={{ width: "100%", maxWidth: "1200px" }}>
                <div style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "15px",
                    flexWrap: "wrap",
                    gap: "10px"
                }}>
                    <div style={{
                        color: darkMode ? "white" : "#1e293b",
                        padding: "12px 24px",
                        borderRadius: "12px",
                        fontWeight: "700",
                        fontSize: "18px",
                        border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #1e293b",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                    }}>
                        {selLanguage ? `Επίπεδο ${currentStage}/3` : `Level ${currentStage}/3`}
                    </div>



                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px"
                    }}>
                        <div style={{ display: "flex", gap: "6px" }}>
                            {[1, 2, 3].map((heartNum) => (
                                <div
                                    key={heartNum}
                                    style={{
                                        fontSize: "24px",
                                        filter: lives >= heartNum
                                            ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                            : "grayscale(100%) opacity(0.3)",
                                    }}
                                >
                                    ❤️
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <br />

                <div style={{
                    borderRadius: "20px",
                    maxWidth: "770px",
                    background: darkMode
                        ? "linear-gradient(135deg, rgba(59, 130, 246, 0.4), rgba(29, 78, 216, 0.4))"
                        : "linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(29, 78, 216, 0.2))",
                    width: "90%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "15px 20px",
                    margin: "0 auto 20px",
                    color: darkMode ? "white" : "#1e293b",
                    fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif"
                }}>
                    {currentStage === 1 &&
                        (selLanguage
                            ? "Γραμμική Αναζήτηση: Σύρετε τους κόμβους με τη σωστή σειρά επίσκεψης για να βρείτε το στοιχείο 12."
                            : "Linear Search: Drag the nodes in the correct visit order to find element 12.")
                    }

                    {currentStage === 2 &&
                        (selLanguage
                            ? "Δυαδική Αναζήτηση: Επιλέξτε κόμβους για να αναζητήσετε το στοιχείο 16 στον ταξινομημένο πίνακα."
                            : "Binary Search: Select nodes to search for element 16 in the sorted array.")
                    }

                    {currentStage === 3 &&
                        (selLanguage
                            ? "Δυαδική Αναζήτηση: Επιλέξτε κόμβους για να αναζητήσετε το στοιχείο 25 που δεν υπάρχει στον πίνακα."
                            : "Binary Search: Select nodes to search for element 25, which does NOT exist in the array.")
                    }

                </div>

                <div style={{ display: "flex", justifyContent: "center", marginTop: "15px" }}>
                    <AnimatePresence>
                        {showMessage && (
                            <motion.div
                                initial={{ opacity: 0, y: -50 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -50 }}
                                style={getMessageStyle(messageType)}
                            >
                                {message}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
                <br />

                <div style={{
                    position: "relative",
                    width: "100%",
                    maxWidth: "700px",
                    aspectRatio: "3/2",
                    border: "3px solid #3b82f6",
                    borderRadius: "20px",
                    margin: "0 auto 30px",
                    background: "#ffffffde",
                    boxShadow: "inset 0 2px 10px rgba(0, 0, 0, 0.1), 0 10px 25px rgba(59, 130, 246, 0.15)",
                    overflowX: "hidden",
                    overflowY: "auto",
                    minHeight: "300px",
                    minWidth: 0
                }}>
                    {currentStage === 1 && renderLinearSearchStage()}
                    {(currentStage === 2 || currentStage === 3) && renderBinarySearchStage()}
                </div>
            </div>
        </div>
    );
};

export default SearchGame;